import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useNavigate } from 'react-router-dom';

const ClientUserGuide: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Руководство пользователя — Тестирование ССТО
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Система тестирования судовой системы охранного оповещения (ССТО/SSAS)
        </Typography>

        <Alert severity="info" sx={{ mt: 2 }}>
          Для подачи заявки нажмите кнопку «Новая заявка» на главной панели личного кабинета.
        </Alert>
      </Paper>

      {/* ===== Секция 1 ===== */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">1. Общие сведения</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography paragraph>
            <strong>Назначение:</strong> автоматизация тестирования судовой системы охранного
            оповещения (ССТО/SSAS) в соответствии с требованиями Международной конвенции СОЛАС.
          </Typography>
          <Typography paragraph>
            <strong>Организация:</strong> ГМСКЦ — Главный морской спасательно-координационный центр.
          </Typography>
          <Typography paragraph>
            <strong>Контакты:</strong>
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Email: od.smrcc@morspas.ru" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Телефон: +7-495-626-10-52" />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>

      {/* ===== Секция 2 ===== */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">2. Подача заявки через веб-форму</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <List>
            <ListItem>
              <ListItemText
                primary="Шаг 1. Войти в личный кабинет"
                secondary="Используйте email и код подтверждения, полученный на почту."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Шаг 2. Нажать «Новая заявка»"
                secondary="Кнопка расположена на главной панели кабинета."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Шаг 3. Заполнить форму"
                secondary={
                  <>
                    • Название судна
                    <br />• MMSI (9 цифр)
                    <br />• IMO (7 цифр)
                    <br />• Номер терминала ССТО (IMN): Inmarsat-C = 9 цифр, Iridium = 15 цифр
                    <br />• Судовладелец
                    <br />• Контактный телефон и контактный email
                    <br />• Дополнительные email-адреса получателей подтверждения: до 3 адресов
                    <br />• Интервал планируемого тестирования: начало теста задается в выбранном
                    часовом поясе, окончание рассчитывается автоматически
                    <br />• Допустимое окно для приёма сигнала: ±24ч от времени подачи заявки.
                  </>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Шаг 4. Отправить заявку"
                secondary="После подтверждения теста письмо с подтверждением отправляется на контактный email и дополнительные адреса, указанные в заявке."
              />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>

      {/* ===== Секция 3 ===== */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">3. Повторная подача заявки</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Где доступно"
                secondary="Компактная кнопка «Отправить снова» доступна в разделах «Мои заявки» и «Панель»."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Что происходит при нажатии"
                secondary="Открывается форма создания новой заявки. Старая заявка не изменяется."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Какие данные переносятся"
                secondary="Данные судна, терминала, судовладельца, контактного лица, телефона, контактного email и дополнительные email-адреса."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Какие данные нужно ввести заново"
                secondary="Дата и время тестирования не копируются. Перед отправкой новой заявки укажите актуальный интервал теста."
              />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>

      {/* ===== Секция 4 ===== */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">4. Проведение тестирования</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography paragraph>
            После регистрации заявки активируйте ССТО на борту в указанный интервал времени.
            Тестовый сигнал будет автоматически обнаружен системой и сопоставлен с вашей заявкой по
            номеру терминала.
          </Typography>
          <Typography paragraph>
            <strong>Поддерживаемые типы терминалов:</strong>
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Inmarsat-C"
                secondary="9 цифр; Inmarsat-C IMN — международный номер терминала"
              />
            </ListItem>
            <ListItem>
              <ListItemText primary="Iridium" secondary="15 цифр, префикс 300" />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>

      {/* ===== Секция 5 ===== */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">5. Получение подтверждения</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography paragraph>
            Подтверждение в формате PDF генерируется автоматически после сопоставления поступившего
            сигнала с заявкой и отправляется на контактный email.
          </Typography>
          <Typography paragraph>
            Скачать подтверждение также можно в разделе «Отчеты» личного кабинета.
          </Typography>
          <Typography paragraph>
            <strong>Содержание PDF:</strong>
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Номер документа и реквизиты ГМСКЦ" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Данные судна и терминала" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Дата и время проведения тестирования" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Подпись оперативного дежурного" />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>

      {/* ===== Секция 6 ===== */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">6. Просмотр статусов</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography paragraph>
            <strong>Этапы жизненного цикла заявки:</strong>
          </Typography>
          <Typography paragraph sx={{ pl: 2 }}>
            Черновик → На рассмотрении → Одобрена → Подтверждена / Отклонена
          </Typography>
          <Typography paragraph>
            В разделе «Мои заявки» доступны фильтрация по судам и датам, а также возможность
            повторной подачи заявки на основе ранее созданной записи.
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<AssignmentIcon />}
          onClick={() => navigate('/client/request/new')}
        >
          Подать заявку
        </Button>
      </Box>
    </Box>
  );
};

export default ClientUserGuide;
